import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Skeleton,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Bell, BellDot, CheckCheck, Inbox, Settings } from "lucide-react";
import { listNotifications } from "../../_services/notifications.service";
import { INotificationListData } from "../../_services/interface/notifications.interface";
import { NotificationContext, useAppData, useConfigGuard } from "../../contexts/AppContext";
import { topNavLayers } from "./topNavLayers";
import { Switch } from "../Input/Switch";
import { TOPNAV_ICON_BUTTON_PROPS } from "./constants";
import { agruparPorData } from "../../pages/NotificationPanel/notificationView";
import { useNotificationActions } from "../../pages/NotificationPanel/useNotificationActions";
import { NotificationRow } from "../../pages/NotificationPanel/components/NotificationRow";

/** Quantas notificações o painel mostra antes de mandar para a central completa. */
const PREVIEW_SIZE = 8;

/** Evento que o backend emite para o destinatário quando cria uma notificação. */
export const NEW_NOTIFICATION_EVENT = "newNotification";

/**
 * Sino da navbar global (GEN-3497; redesenho GEN-4100/4101).
 *
 * - Clicar no aviso vai DIRETO ao objeto (certidão, mensagem, atividade, Dívida Ativa) e marca como lido
 *   — decisão do Thiago, 17/10/2026. Antes o item não era clicável e tudo ficava "não lido".
 * - Um sinal só de não lida (ponto + título semibold); urgência no ícone do produto.
 * - Ação principal por tipo e menu "⋯" (detalhes, lida/não lida, arquivar), com Desfazer.
 * - "Marcar todas como lidas" no servidor (sem o teto de 200 da versão anterior).
 *
 * O socket consumido é o MESMO criado no AppLayout — a barra não abre conexão nova.
 */
export const AlertsBell = () => {
  const { collaborator, socket } = useAppData();
  const { requestNavigation } = useConfigGuard();
  const notificationValues = useContext(NotificationContext);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [preview, setPreview] = useState<{
    guidCollaborator?: string;
    soNaoLidas?: boolean;
    items: INotificationListData[];
  }>({ items: [] });
  const [loading, setLoading] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const [soNaoLidas, setSoNaoLidas] = useState(false);
  const [marcandoTodas, setMarcandoTodas] = useState(false);

  const unread = notificationValues.unread ?? 0;
  const guidCollaborator = collaborator?.guid_collaborator;
  const colaboradorAtual = useRef(guidCollaborator);
  colaboradorAtual.current = guidCollaborator;
  const consultaCorresponde = preview.guidCollaborator === guidCollaborator && preview.soNaoLidas === soNaoLidas;
  const items = consultaCorresponde ? preview.items : [];
  const aguardandoConsulta = !!guidCollaborator && !consultaCorresponde;

  const consultaAtual = useRef(0);
  const loadPreview = useCallback(async () => {
    if (!guidCollaborator) return;
    const consulta = ++consultaAtual.current;
    setLoading(true);
    setFalhou(false);
    const result = await listNotifications(guidCollaborator, {
      folder: "INBOX",
      ...(soNaoLidas ? { indicator: "unread" as const } : {}),
      page: 1,
      row_by_page: PREVIEW_SIZE,
      order_field: "createdAt",
      order_direction: "desc",
    });
    if (consulta !== consultaAtual.current || colaboradorAtual.current !== guidCollaborator) return;
    if (result.status === 200) {
      setPreview({
        guidCollaborator,
        soNaoLidas,
        items: (result.response as { data?: INotificationListData[] })?.data ?? [],
      });
    } else {
      setPreview({ guidCollaborator, soNaoLidas, items: [] });
      setFalhou(true);
    }
    setLoading(false);
  }, [guidCollaborator, soNaoLidas]);

  const actions = useNotificationActions(() => {
    if (isOpen) void loadPreview();
  });

  // Só busca a lista quando o painel abre: o contador já vem do contexto.
  useEffect(() => {
    if (isOpen) void loadPreview();
  }, [isOpen, loadPreview]);

  // --- tempo real ------------------------------------------------------------
  // Handler num ref: UMA assinatura por socket (sem isso cada render somaria um listener).
  const onNewNotificationRef = useRef<() => void>(() => undefined);
  onNewNotificationRef.current = () => {
    void actions.refreshCount();
    if (isOpen) void loadPreview();
  };

  useEffect(() => {
    if (!socket) return undefined;
    const handler = () => onNewNotificationRef.current();
    socket.on(NEW_NOTIFICATION_EVENT, handler);
    return () => {
      socket.off(NEW_NOTIFICATION_EVENT, handler);
    };
  }, [socket]);

  const irPara = (path: string) => {
    onClose();
    requestNavigation(path);
  };

  const BellIcon = unread > 0 ? BellDot : Bell;
  const badgeLabel = unread > 99 ? "99+" : String(unread);
  const grupos = agruparPorData(items);

  return (
    <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end" isLazy>
      <PopoverTrigger>
        <Box position="relative" display="inline-flex">
          <Tooltip label="Notificações" openDelay={600}>
            <IconButton
              aria-label={
                unread > 0
                  ? `Notificações, ${unread} não lida${unread > 1 ? "s" : ""}`
                  : "Notificações, nenhuma não lida"
              }
              {...TOPNAV_ICON_BUTTON_PROPS}
              icon={<BellIcon size={17} />}
            />
          </Tooltip>
          {unread > 0 ? (
            <Flex
              // aria-hidden: o número já está no aria-label do botão.
              aria-hidden="true"
              position="absolute"
              // -4px mantém o contador acima do meio do ícone com respiro até o topo da barra (r anterior).
              top="-4px"
              right="-4px"
              minW="18px"
              h="18px"
              px="4px"
              borderRadius="9px"
              // Tom leve (pedido do Thiago em 25/08): vermelho escuro sobre rosa claro.
              backgroundColor="#FEE2E2"
              color="#B42318"
              borderWidth="1px"
              borderColor="#FECDCA"
              fontSize="10px"
              fontWeight="500"
              align="center"
              justify="center"
              pointerEvents="none"
            >
              {badgeLabel}
            </Flex>
          ) : null}
        </Box>
      </PopoverTrigger>

      <PopoverContent
        zIndex={topNavLayers.panel}
        w="400px"
        maxW="calc(100vw - 16px)"
        boxShadow="lg"
        _focusVisible={{ outline: "none" }}
      >
        <PopoverHeader borderBottomWidth="1px" py="10px">
          <Flex align="center" justify="space-between" gap="8px">
            <Flex direction="column">
              <Text fontSize="14px" fontWeight="600" color="#303033">
                Notificações
              </Text>
              {/* aria-live: quem usa leitor de tela ouve a contagem mudar em tempo real */}
              <Text fontSize="12px" color="#77777B" aria-live="polite">
                {unread > 0 ? `${unread} não lida${unread > 1 ? "s" : ""} nos últimos 30 dias` : "Tudo em dia"}
              </Text>
            </Flex>
            <Flex align="center" gap="4px">
              <Flex align="center" gap="6px" mr="4px">
                <Text as="button" type="button" fontSize="12px" color="#46464A" onClick={() => setSoNaoLidas((value) => !value)}>
                  Só não lidas
                </Text>
                <Switch
                  width="28px"
                  isActive={soNaoLidas}
                  onChange={setSoNaoLidas}
                  ariaLabel="Mostrar só as não lidas"
                />
              </Flex>
              {/* Desabilitado quando não há o que fazer, em vez de sumir (o painel não muda de forma). */}
              <Tooltip label="Marcar todas como lidas" openDelay={500}>
                <IconButton
                  aria-label="Marcar todas como lidas"
                  size="xs"
                  variant="ghost"
                  color="#46464A"
                  icon={<CheckCheck size={15} />}
                  isDisabled={unread === 0 || marcandoTodas}
                  isLoading={marcandoTodas}
                  onClick={async () => {
                    setMarcandoTodas(true);
                    try {
                      await actions.marcarTodasComoLidas({ folder: "INBOX" });
                    } finally {
                      setMarcandoTodas(false);
                    }
                  }}
                />
              </Tooltip>
              <Tooltip label="Preferências de notificação" openDelay={500}>
                <IconButton
                  aria-label="Preferências de notificação"
                  size="xs"
                  variant="ghost"
                  color="#46464A"
                  icon={<Settings size={15} />}
                  onClick={() => irPara("/configuracoes/notificacoes")}
                />
              </Tooltip>
            </Flex>
          </Flex>
        </PopoverHeader>

        <PopoverBody p="0" maxH="420px" overflowY="auto" className="scrollbarsidebar">
          {(loading || aguardandoConsulta) && items.length === 0 ? (
            <Flex direction="column" gap="14px" p="14px" aria-busy="true">
              {[0, 1, 2].map((n) => (
                <Flex key={n} gap="10px">
                  <Skeleton w="28px" h="28px" borderRadius="7px" fadeDuration={0.15} />
                  <Flex direction="column" gap="6px" flex="1">
                    <Skeleton h="14px" w="80%" fadeDuration={0.15} />
                    <Skeleton h="12px" w="55%" fadeDuration={0.15} />
                  </Flex>
                </Flex>
              ))}
            </Flex>
          ) : falhou ? (
            <Flex direction="column" align="center" gap="6px" py="28px" px="16px" textAlign="center">
              <Text fontSize="14px" color="#46464A">
                Não foi possível carregar as notificações.
              </Text>
              <Button size="sm" variant="ghost" fontSize="13px" onClick={() => void loadPreview()}>
                Recarregar
              </Button>
            </Flex>
          ) : items.length === 0 ? (
            <Flex direction="column" align="center" gap="4px" py="28px" px="16px" color="gray.400">
              <Inbox size={28} strokeWidth={1.5} />
              <Text fontSize="14px" color="#46464A">
                {soNaoLidas ? "Tudo em dia" : "Nenhuma notificação por aqui."}
              </Text>
              <Text fontSize="12px" color="#77777B" textAlign="center">
                Avisos sobre certidões, mensagens e cadastros aparecem neste painel.
              </Text>
            </Flex>
          ) : (
            grupos.map(({ grupo, itens }) => (
              <Box key={grupo}>
                <Text
                  fontSize="10.5px"
                  fontWeight="600"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  color="#77777B"
                  px="14px"
                  pt="10px"
                  pb="2px"
                >
                  {grupo}
                </Text>
                {itens.map((item) => (
                  <Box key={item.guid_notification} borderBottom="1px solid #EDF2F7">
                    <NotificationRow
                      item={item}
                      variant="bell"
                      onOpen={() => actions.abrirDestino(item, onClose)}
                      onPrimaryAction={() => actions.executarAcao(item, onClose)}
                      onShowInInbox={() => irPara(`/notificacoes/${item.guid_notification}`)}
                      onToggleRead={() => {
                        void actions.marcarLida(item, !item.readInSystem);
                      }}
                      onToggleArchive={() => {
                        void actions.moverPasta([item.guid_notification], true);
                      }}
                    />
                  </Box>
                ))}
              </Box>
            ))
          )}
        </PopoverBody>

        <Flex borderTopWidth="1px" p="8px">
          <Button variant="ghost" size="sm" w="100%" fontSize="13px" onClick={() => irPara("/notificacoes")}>
            Ver todas as notificações
          </Button>
        </Flex>
      </PopoverContent>
    </Popover>
  );
};
